/**
 * @fileoverview React component for displaying QR codes
 * 
 * @purpose Client-side QR code display component
 * @usage Use in admin UI or other client components to display QR codes
 * @ownership Core shared components
 * @dependencies
 * - qrcode.react or react-qr-code
 * @restrictions
 * - Client-side only
 */

'use client'

import { ERR_CHAT_500 } from '@korvo/core/lib/error-codes'
import { KorvoError } from '@korvo/core/lib/korvo-error'
import { useEffect, useState } from 'react'

type QRCodeProps = {
  /** URL or text to encode in QR code */
  value: string
  /** Size of QR code in pixels */
  size?: number
  /** Error correction level */
  level?: 'L' | 'M' | 'Q' | 'H'
  /** Additional CSS classes */
  className?: string
}

/**
 * QR Code component using dynamic import for code splitting
 * 
 * Falls back to server-side generation if client library not available
 */
export function QRCode({
  value,
  size = 200,
  level = 'M',
  className,
}: QRCodeProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Generate QR code on client side
    const generateQR = async () => {
      try {
        // Try to use server-side generation via API
        const response = await fetch('/api/qr-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: value, size, level }),
        })

        if (response.ok) {
          const data = await response.json()
          setQrCodeDataUrl(data.dataUrl)
        } else {
          throw new KorvoError(ERR_CHAT_500, 1, {
            message: 'Failed to generate QR code',
            status: response.status,
          });
        }
      } catch (err) {
        console.error('QR code generation error:', err)
        setError('Failed to generate QR code')
      }
    }

    generateQR()
  }, [value, size, level])

  if (error) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--input-border)',
          borderRadius: '4px',
          color: 'var(--foreground-muted)',
        }}
      >
        QR Code Error
      </div>
    )
  }

  if (!qrCodeDataUrl) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--input-border)',
          borderRadius: '4px',
        }}
      >
        <div
          style={{
            width: size * 0.3,
            height: size * 0.3,
            border: '2px solid var(--foreground-muted)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    )
  }

  return (
    <img
      src={qrCodeDataUrl}
      alt="QR Code"
      className={className}
      style={{
        width: size,
        height: size,
        display: 'block',
      }}
    />
  )
}
